<template>
  <Toaster />
  <div>
    <form @submit.prevent="onSubmit" class="volunteer-registration-form">
      <h3>Volunteer Name</h3>
      <div class="field-group">
        <FormField v-slot="{ componentField }" name="firstName">
          <FormItem>
            <FormControl>
              <Input type="text" :placeholder="model.translations['volunteerRegistration.Form.FirstName']"
                v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="lastName">
          <FormItem>
            <FormControl>
              <Input type="text" :placeholder="model.translations['volunteerRegistration.Form.LastName']"
                v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
      <h3>Contact Info</h3>
      <div class="field-group">
        <FormField v-slot="{ componentField }" name="contactNumber">
          <FormItem>
            <FormControl>
              <Input type="text" :placeholder="model.translations['volunteerRegistration.Form.ContactInputPlaceholder']"
                v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="email">
          <FormItem>
            <FormControl>
              <Input type="text" :placeholder="model.translations['volunteerRegistration.Form.EmailAddress']"
                v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="postcode">
          <FormItem>
            <FormControl>
              <Input type="text" :placeholder="model.translations['volunteerRegistration.Form.Postcode']"
                v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
      <h3>Select Charity</h3>
      <FormField v-slot="{ componentField }" name="hospiceId">
        <FormItem>
          <FormControl>
            <Select v-bind="componentField">
              <FormControl>
                <SelectTrigger class="w-full md:w-[400px]">
                  <SelectValue placeholder="Select a charity" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Charities</SelectLabel>
                  <SelectItem v-for="item in model.hospiceList" :value="item.value">
                    {{ item.text }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
      <h3>Emergency Info</h3>
      <div class="field-group">
        <FormField v-slot="{ componentField }" name="nextOfKin">
          <FormItem>
            <FormControl>
              <Input type="text" :placeholder="model.translations['volunteerRegistration.Form.NextOfKin']"
                v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <FormField v-slot="{ componentField }" name="nextOfKinContactNumber">
          <FormItem>
            <FormControl>
              <Input type="text" :placeholder="model.translations['volunteerRegistration.Form.NextOfKinContactNumber']"
                v-bind="componentField" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
      <h3>Privacy Policy</h3>
      <FormField v-slot="{ value, handleChange }" name="isPhotoUsageConfirmed">
        <FormItem class="checkbox-field">
          <FormControl>
            <Checkbox :model-value="value" @update:model-value="handleChange" />
          </FormControl>
          <FormLabel>{{ model.translations["volunteerRegistration.Form.OptionalPrivacyPolicy"] }}</FormLabel>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ value, handleChange }" name="isObligationConfirmed">
        <FormItem class="checkbox-field">
          <FormControl>
            <Checkbox :model-value="value" @update:model-value="handleChange" />
          </FormControl>
          <FormLabel>{{ model.translations["volunteerRegistration.Form.ObligatoryPrivacyPolicy"] }}</FormLabel>
          <FormMessage />
        </FormItem>
      </FormField>
      <FormField v-slot="{ value, handleChange }" name="isCodeOfConductConfirmed">
        <FormItem class="checkbox-field">
          <FormControl>
            <Checkbox :model-value="value" @update:model-value="handleChange" :disabled="!isCodeOfConductLinkClicked" />
          </FormControl>
          <FormMessage />
          <FormLabel style="opacity: 1;">
            <div class="policy-text">
              I agree to adhere to the
              Volunteer Code of Conduct & Health and Safety Policies.
              NOTE: Volunteer registration will not be validated until these policies have been viewed.
              <Button as-child variant="outline">
                <a href="/static/pdfs/CodeOfConduct.pdf" target="_blank" @click="codeOfConductLinkClicked">
                  View Code of Conduct
                </a>
              </Button>
            </div>
          </FormLabel>
        </FormItem>
      </FormField>
      <div class="buttons">
        <LoadingButton :is-loading="isPending" type="submit">
          Submit
        </LoadingButton>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, } from "vue"
import { load, ReCaptchaInstance } from "recaptcha-v3"
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import type { IVolunteerRegistrationFormProps } from "./VolunteerRegistrationForm.model";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel
} from "@/components/ui/select"
import { useForm } from "vee-validate"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useMutation } from '@tanstack/vue-query'
import { volunteerRegistration, type IVolunteerRegistrationRequest } from "@/api";
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'vue-sonner'
import LoadingButton from "@/components/LoadingButton/LoadingButton.vue";
const { model } = defineProps<IVolunteerRegistrationFormProps>();

const formSchema = toTypedSchema(z.object({
  firstName: z.string(model.translations["generic.FieldRequired"]).min(2),
  lastName: z.string(model.translations["generic.FieldRequired"]).min(2),
  email: z.email(model.translations["generic.FieldRequired"]),
  contactNumber: z.string(model.translations["generic.FieldRequired"]).min(10),
  postcode: z.string(model.translations["generic.FieldRequired"]).min(2),
  hospiceId: z.string(model.translations["generic.FieldRequired"]),
  nextOfKin: z.string(model.translations["generic.FieldRequired"]).min(2),
  nextOfKinContactNumber: z.string().min(10),
  isObligationConfirmed: z.boolean(model.translations["generic.FieldRequired"]),
  isCodeOfConductConfirmed: z.boolean(model.translations["generic.FieldRequired"]),
  isPhotoUsageConfirmed: z.boolean(model.translations["generic.FieldRequired"]).optional(),
}))

const { handleSubmit } = useForm({
  validationSchema: formSchema,
})


let recaptcha: ReCaptchaInstance;
let isCodeOfConductLinkClicked = ref(false)


onMounted(() => {
  load(model.reCaptcha?.siteKey ?? "").then(async (recaptchaInstance) => {
    recaptcha = recaptchaInstance
  })
})


const codeOfConductLinkClicked = () => {
  isCodeOfConductLinkClicked.value = true
}

const onSubmit = handleSubmit(async (values) => {
  recaptcha.execute("register").then((token) => {
    registerVolunteer({
      ...values,
      captchaToken: token,
    })
  })
})

const { mutate: registerVolunteer, isPending } = useMutation({
  mutationFn: (data: IVolunteerRegistrationRequest) => volunteerRegistration(model.submitFormUrl ?? "", data),
  onSuccess: (data) => {
    if (!data.volunteerRegistered) {
      toast("Volunteer not registered", { description: data.volunteerRegistrationMessage })
      return;
    }
    const url = data.volunteerRegisteredUrl
    window.location.assign(url)
  },
  onError: (error) => {
    console.log(model.translations["generic.Error"])
  },
})


</script>

<style lang="scss" scoped>
.volunteer-registration-form {
  display: flex;
  flex-direction: column;
  gap: 20px
}

.field-group {
  >div {
    flex: 1
  }

  display: flex;
  gap: 1rem;
  flex-direction: row;
}

@media (min-width: 768px) {
  .field-group {
    flex-direction: row;
  }
}

.policy-text {
  display: flex;
  gap: 10px;
  align-items: center;
}

.checkbox-field {
  display: flex;
  gap: 10px;
  align-items: center;
}

.buttons {
  display: flex;
  justify-content: flex-end;
}
</style>
